import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usersApi, type StorageUsage } from '../../api/users.api';
import Section from '../ui/Section';
import { formatBytes } from '../../utils/formatBytes';

/** ADR-0055: how much of the photo quota is used. */
export default function StorageSection() {
  const { t, i18n } = useTranslation();
  const [usage, setUsage] = useState<StorageUsage | null>(null);

  useEffect(() => {
    usersApi.getStorageUsage().then(setUsage).catch(() => setUsage(null));
  }, []);

  if (!usage) return null;
  const pct = Math.min(100, Math.round((usage.usedBytes / usage.quotaBytes) * 100));
  const full = usage.usedBytes >= usage.quotaBytes;

  return (
    <Section title={t('account.storage.title')}>
      <div className="px-4 py-3 space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-600 dark:text-slate-400">{t('account.storage.photos')}</span>
          <span className={`font-semibold ${full ? 'text-red-600 dark:text-red-400' : 'text-teal-600 dark:text-teal-400'}`}>
            {t('account.storage.used', { used: formatBytes(usage.usedBytes, i18n.language), quota: formatBytes(usage.quotaBytes, i18n.language) })}
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
          <div className={`h-full rounded-full ${full ? 'bg-red-500' : 'bg-teal-600'}`} style={{ width: `${pct}%` }} />
        </div>
        {full && <p className="text-xs text-red-600 dark:text-red-400">{t('account.storage.full')}</p>}
      </div>
    </Section>
  );
}
